import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Usuario } from '../../../../domain/usuario.domain';
import { SecurityService } from '../../../../services/security/security.service';
import { environment } from '../../../../../environments/environment';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AuthImagePipe } from '../../../../pipes/auth-image.pipe';
import { AvatarCacheService } from '../../../../services/avatar-cache/avatar-cache.service';

@Component({
  selector: 'app-user-meta-card',
  imports: [
    CommonModule,
    ToastModule,
    AuthImagePipe,
  ],
  providers: [MessageService],
  templateUrl: './user-meta-card.component.html',
  styles: ``
})
export class UserMetaCardComponent implements OnChanges {
  @Input() usuario: Usuario = {};
  
  private securityService = inject(SecurityService);
  private messageService = inject(MessageService);
  private avatarCache = inject(AvatarCacheService);

  selectedFile: File | null = null;
  previewUrl: string | null = null;
  isUploading = false;
  showPreview = false;
  avatarUrl: string | null = null;
  readonly placeholderAvatarUrl = '/images/user/owner.png';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['usuario']) {
      this.updateAvatarUrl();
    }
  }

  private updateAvatarUrl(): void {
    const mediaId = this.usuario?.media?.id;
    if (mediaId) {
      const baseApi = environment.URL_API.replace(/\/$/, '');
      this.avatarUrl = `${baseApi}/file-manager?action=get&idMedia=${mediaId}`;
      return;
    }
    this.avatarUrl = null;
  }

  getNombreCompleto(): string {
    if (this.usuario?.fullName) {
      return this.usuario.fullName;
    }
    if (this.usuario?.name && this.usuario?.lastName) {
      return `${this.usuario.name} ${this.usuario.lastName}`;
    }
    return this.usuario?.username || 'Usuario';
  }

  getRoles(): string {
    if (this.usuario?.roles && this.usuario.roles.length > 0) {
      return this.usuario.roles
        .filter(role => role !== 'ROLE_USER')
        .map(role => role.replace('ROLE_', ''))
        .join(', ');
    }
    return 'Usuario';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Validar tipo de archivo
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'El archivo debe ser una imagen (JPEG, PNG, GIF, WEBP)'
        });
        input.value = '';
        return;
      }

      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'El archivo no debe superar los 5MB'
        });
        input.value = '';
        return;
      }

      this.selectedFile = file;
      
      // Crear preview de la imagen
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        this.previewUrl = e.target?.result as string;
        this.showPreview = true;
      };
      reader.readAsDataURL(file);
    }
  }

  confirmUpload(): void {
    if (!this.selectedFile || !this.usuario?.id) {
      return;
    }

    this.isUploading = true;
    const formData = new FormData();
    formData.append('file', this.selectedFile);
    const previousAvatarUrl = this.avatarUrl;

    this.securityService.subirAvatar(this.usuario.id, formData).subscribe({
      next: (response) => {
        console.log('✅ Respuesta completa del servidor:', response);
        console.log('📦 Media recibido:', response?.media);
        console.log('🖼️ ContentUrl:', response?.media?.contentUrl);
        
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Avatar actualizado correctamente'
        });
        this.isUploading = false;
        this.selectedFile = null;
        this.showPreview = false;
        this.previewUrl = null;
        
        // Actualizar el usuario en el componente con TODOS los datos del response
        if (response) {
          this.usuario = { ...this.usuario, ...response };
          console.log('👤 Usuario actualizado en componente:', this.usuario);
          this.updateAvatarUrl();
          if (previousAvatarUrl && previousAvatarUrl !== this.avatarUrl) {
            this.avatarCache.invalidate(previousAvatarUrl);
          }
          console.log('🔗 Avatar URL que se mostrará:', this.avatarUrl);
        }
        
        // Limpiar el input
        const fileInput = document.getElementById('avatar-upload') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      },
      error: (error) => {
        console.error('Error al subir avatar:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo actualizar el avatar'
        });
        this.isUploading = false;
        this.cancelUpload();
      }
    });
  }

  cancelUpload(): void {
    this.selectedFile = null;
    this.previewUrl = null;
    this.showPreview = false;
    
    // Limpiar el input
    const fileInput = document.getElementById('avatar-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  triggerFileInput(): void {
    if (!this.showPreview && !this.isUploading) {
      const fileInput = document.getElementById('avatar-upload') as HTMLInputElement;
      if (fileInput) {
        fileInput.click();
      }
    }
  }
}
